import { Component, DestroyRef, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { DatePicker } from 'primeng/datepicker';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { catchError, of } from 'rxjs';
import {
  CreatePresalesRaidItemDto,
  PresalesRaidItem,
  PresalesRaidItemService,
} from '../../services/presales-raid-item.service';

@Component({
  selector: 'app-presales-raid',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    Tag,
    Tooltip,
    Dialog,
    InputText,
    Select,
    DatePicker,
    ConfirmDialog,
  ],
  templateUrl: './presales-raid.component.html',
  styleUrl: './presales-raid.component.scss',
  providers: [ConfirmationService],
})
export class PresalesRaidComponent implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef);

  @Input() presalesProjectId: string = '';

  items: PresalesRaidItem[] = [];
  selectedItem: PresalesRaidItem | null = null;
  displayItemDialog = false;
  isEditItemMode = false;
  identifiedDate: Date | null = null;
  targetDate: Date | null = null;
  dateClosed: Date | null = null;
  loading = false;
  saveInFlight = false;
  fieldTouched: Record<string, boolean> = {};

  typeOptions = [
    { label: 'Risk', value: 'Risk' },
    { label: 'Assumption', value: 'Assumption' },
    { label: 'Issue', value: 'Issue' },
    { label: 'Dependency', value: 'Dependency' },
  ];

  impactOptions = [
    { label: 'High', value: 'High' },
    { label: 'Medium', value: 'Medium' },
    { label: 'Low', value: 'Low' },
  ];

  statusOptions = [
    { label: 'Open', value: 'Open' },
    { label: 'Off Track', value: 'Off Track' },
    { label: 'Closed', value: 'Closed' },
  ];

  constructor(
    private raidService: PresalesRaidItemService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
  ) {}

  ngOnInit() {
    if (this.presalesProjectId) this.loadItems();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['presalesProjectId'] && this.presalesProjectId) this.loadItems();
  }

  loadItems() {
    if (!this.presalesProjectId) return;
    this.loading = true;
    this.raidService
      .getByPresalesProjectId(this.presalesProjectId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((err) => {
          if (err?.status !== 404) {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to load RAID items.',
            });
          }
          return of([] as PresalesRaidItem[]);
        }),
      )
      .subscribe((items) => {
        this.items = items;
        this.loading = false;
      });
  }

  showAddItemDialog() {
    if (!this.presalesProjectId) return;
    this.selectedItem = {
      id: '',
      presalesProjectId: this.presalesProjectId,
      itemNumber: this.generateItemNumber(),
      type: 'Risk',
      description: '',
      identifiedDate: this.formatDateToLocalString(new Date()),
      impact: 'High',
      owner: '',
      status: 'Open',
      mitigationPlan: '',
      remark: '',
    };
    this.identifiedDate = new Date();
    this.targetDate = null;
    this.dateClosed = null;
    this.fieldTouched = {};
    this.isEditItemMode = false;
    this.displayItemDialog = true;
  }

  showEditItemDialog(item: PresalesRaidItem) {
    this.selectedItem = { ...item };
    this.identifiedDate = item.identifiedDate ? this.parseLocalDateString(item.identifiedDate) : null;
    this.targetDate = item.targetDate ? this.parseLocalDateString(item.targetDate) : null;
    this.dateClosed = item.dateClosed ? this.parseLocalDateString(item.dateClosed) : null;
    this.fieldTouched = {};
    this.isEditItemMode = true;
    this.displayItemDialog = true;
  }

  saveItem() {
    if (this.saveInFlight) return;
    this.fieldTouched['description'] = true;
    this.fieldTouched['owner'] = true;
    if (!this.selectedItem || !this.selectedItem.description.trim() || !this.selectedItem.owner.trim()) return;

    if (this.identifiedDate) this.selectedItem.identifiedDate = this.formatDateToLocalString(this.identifiedDate);
    this.selectedItem.targetDate = this.targetDate ? this.formatDateToLocalString(this.targetDate) : undefined;
    this.selectedItem.dateClosed = this.dateClosed ? this.formatDateToLocalString(this.dateClosed) : undefined;

    this.saveInFlight = true;

    if (this.isEditItemMode) {
      this.raidService
        .updateItem(this.selectedItem.id, this.selectedItem)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (updated) => {
            this.saveInFlight = false;
            const idx = this.items.findIndex((i) => i.id === updated.id);
            if (idx !== -1) this.items[idx] = updated;
            this.messageService.add({
              severity: 'success',
              summary: 'Updated',
              detail: 'RAID item updated.',
            });
            this.displayItemDialog = false;
            this.selectedItem = null;
          },
          error: (err) => {
            this.saveInFlight = false;
            const detail =
              err?.error?.details || err?.error?.message || err?.error?.title || 'Failed to update RAID item.';
            this.messageService.add({ severity: 'error', summary: 'Error', detail });
          },
        });
    } else {
      const dto: CreatePresalesRaidItemDto = {
        presalesProjectId: this.presalesProjectId,
        itemNumber: this.selectedItem.itemNumber,
        type: this.selectedItem.type,
        description: this.selectedItem.description,
        identifiedDate: this.selectedItem.identifiedDate,
        impact: this.selectedItem.impact,
        owner: this.selectedItem.owner,
        targetDate: this.selectedItem.targetDate,
        status: this.selectedItem.status,
        mitigationPlan: this.selectedItem.mitigationPlan,
        dateClosed: this.selectedItem.dateClosed,
        remark: this.selectedItem.remark,
      };
      this.raidService
        .addItem(dto)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (created) => {
            this.saveInFlight = false;
            this.items = [...this.items, created];
            this.messageService.add({
              severity: 'success',
              summary: 'Added',
              detail: 'RAID item added.',
            });
            this.displayItemDialog = false;
            this.selectedItem = null;
          },
          error: (err) => {
            this.saveInFlight = false;
            const detail =
              err?.error?.details || err?.error?.message || err?.error?.title || 'Failed to add RAID item.';
            this.messageService.add({ severity: 'error', summary: 'Error', detail });
          },
        });
    }
  }

  confirmDelete(item: PresalesRaidItem) {
    this.confirmationService.confirm({
      header: 'Delete RAID item?',
      message: `"${item.description}" will be removed permanently.`,
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteItem(item),
    });
  }

  private deleteItem(item: PresalesRaidItem) {
    this.raidService
      .deleteItem(item.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.items = this.items.filter((i) => i.id !== item.id);
          this.messageService.add({
            severity: 'success',
            summary: 'Deleted',
            detail: 'RAID item removed.',
          });
        },
        error: (err) => {
          const detail =
            err?.error?.details || err?.error?.message || err?.error?.title || 'Failed to delete RAID item.';
          this.messageService.add({ severity: 'error', summary: 'Error', detail });
        },
      });
  }

  generateItemNumber(): string {
    if (this.items.length === 0) return '00001';
    const maxId = Math.max(...this.items.map((i) => parseInt(i.itemNumber.replace(/\D/g, ''), 10) || 0));
    return String(maxId + 1).padStart(5, '0');
  }

  private parseLocalDateString(dateStr: string): Date | null {
    const parts = dateStr.trim().split('-');
    if (parts.length !== 3) return null;
    const [y, m, d] = parts.map((p) => parseInt(p, 10));
    if (isNaN(y) || isNaN(m) || isNaN(d)) return null;
    const date = new Date(y, m - 1, d);
    return isNaN(date.getTime()) ? null : date;
  }

  private formatDateToLocalString(date: Date): string {
    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    const d = date.getDate();
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }

  formatDate(dateString?: string): string {
    if (!dateString) return '-';
    const date = this.parseLocalDateString(dateString);
    if (!date) return '-';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  getImpactSeverity(impact: string): 'success' | 'warn' | 'danger' | 'secondary' {
    switch (impact) {
      case 'High':
        return 'danger';
      case 'Medium':
        return 'warn';
      case 'Low':
        return 'success';
      default:
        return 'secondary';
    }
  }

  getStatusSeverity(status: string): 'success' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case 'Closed':
        return 'success';
      case 'Off Track':
        return 'warn';
      case 'Open':
        return 'danger';
      default:
        return 'secondary';
    }
  }
}
